// Table
import Table from '@/components/ui/table/Table'

// Mantine
import { Anchor } from '@mantine/core'

// Router
import Link from '@/components/ui/RouterLink'

// Icons
import { Qrcode } from 'tabler-icons-react'

// Dayjs
import dayjs from 'dayjs'

// API
import { useApiSWRReference } from '@/hooks/useApiSWR'

// Cells
const CodeCell = (item) => (
    <div className="table-cell align-middle	p-5 w-[200px]">
        <Anchor href={'/warranties/' + item.code}>
            <div className="flex gap-2 items-center">
                <Qrcode />
                <span>{item.code}</span>
            </div>
        </Anchor>
    </div>
)

// Fixed width and one line: component names run long ("Heures du Monde -
// Worldtimer - Oeil de Tigre") and wrapping them tripled the row height.
const ComponentCell = (item) => (
    <div className="table-cell p-5 text-gray-500">
        <div className="w-[320px] truncate" title={item?.component?.name}>
            {item?.component?.name}
        </div>
    </div>
)

const WarrantyStartCell = (item) => (
    <div className="table-cell p-5 text-gray-500 text-sm w-[170px] whitespace-nowrap">
        {dayjs(item.warranty_start_at).format('ll')}
    </div>
)

const WarrantyEndCell = (item) => (
    <div className="table-cell p-5 text-gray-500 text-sm w-[170px] whitespace-nowrap">
        {dayjs(item.warranty_end_at).format('ll')}
    </div>
)

// Who registered the card: either a retailer of the `retailers` table, or a
// backoffice user. Only the retailer gets a link — a profile is just a name.
const ByCell = (item) => (
    <div className="table-cell p-5 text-gray-500 text-sm">
        <div
            className="w-[220px] truncate"
            title={item?.retailer?.name ?? item?.profile?.name}
        >
            {item?.retailer ? (
                <Anchor
                    size="sm"
                    component={Link}
                    href={`/retailers/${item.retailer.id}`}
                >
                    {item.retailer.name}
                </Anchor>
            ) : (
                item?.profile?.name
            )}
        </div>
    </div>
)

const CreatedAtCell = (item) => (
    <div className="table-cell p-5 text-gray-500 text-sm w-[240px] whitespace-nowrap">
        {dayjs(item.created_at).calendar()}
    </div>
)

export default function WarrantyTable() {
    // Available columns for the table
    const availableColumns = [
        {
            name: 'Code',
            key: 'code',
            display: 'mandatory',
            sortable: true,
            component: CodeCell,
        },
        {
            name: 'Composant',
            key: 'component_id',
            display: 'optional_default',
            sortable: true,
            component: ComponentCell,
        },
        {
            name: 'Début',
            key: 'warranty_start_at',
            display: 'optional_default',
            sortable: true,
            component: WarrantyStartCell,
        },
        {
            name: 'Fin',
            key: 'warranty_end_at',
            display: 'optional_default',
            sortable: true,
            component: WarrantyEndCell,
        },
        {
            // Not sortable: the column reads from two different tables, and
            // ordering on either id would sort by nothing the user can see.
            name: 'Par',
            key: 'by',
            display: 'optional_default',
            sortable: false,
            component: ByCell,
        },
        {
            name: 'Création',
            key: 'created_at',
            display: 'optional_default',
            sortable: true,
            component: CreatedAtCell,
        },
    ]

    // Load filters options
    const { data: components } = useApiSWRReference('/components/list', {
        category: 'watch_head',
    })

    // Load profiles. `all` and not `mentionable`: a warranty can have been
    // created by someone since disabled, and the filter must still name them.
    const { data: profilesData } = useApiSWRReference('/profiles/all')
    const profiles = profilesData?.all

    // Available filters for the table
    const availableFilters = [
        {
            name: 'Composant',
            key: 'component_id',
            type: 'multiselect',
            placeholder:
                'Aquascaphe Classic - Bleu Gilt, Hermétique Tourer - Beige...',
            options: components?.map((component) => ({
                label: component.name,
                value: component.id.toString(),
            })),
        },
        {
            name: 'Utilisateur',
            key: 'user_id',
            type: 'multiselect',
            placeholder: 'Filter par utilisateur...',
            options: profiles?.map((profile) => ({
                label: profile.name,
                value: profile.id,
            })),
        },
    ]

    return (
        <Table
            searchable={true}
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            engineApiEndpoint="/warranties"
            defaultSortColumn="created_at"
            defaultSortDirection="desc"
        />
    )
}
